import {
  CategoricalLabelEntry,
  resolveCategoryColor,
} from "../projects/layers/categorical_layer_colors"
import { CategoricalTileGrid } from "../projects/modelling/tile_grid"
import type { DatasetLayer } from "../projects/state"
import { PaintClass } from "./utils/types"

export const DEFAULT_CLASS_PALETTE = [
  "#ef4444", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6",
  "#ec4899", "#14b8a6", "#f97316", "#22c55e", "#6366f1",
]

export function rgbaComponentsToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map(channel => {
    const hex = Math.round(channel).toString(16)
    return hex.length === 1 ? `0${hex}` : hex
  }).join("")}`
}

/** Merge paint classes from a categorical dataset layer's labels and style colours. */
export function mergePaintClassesFromCategoricalLayer(
  existingClasses: PaintClass[],
  labelEntries: CategoricalLabelEntry[],
  layerStyle: Pick<DatasetLayer, "colors" | "colorByLabel">
): PaintClass[] {
  const mergedClasses = [...existingClasses]
  let newClassCounter = 0

  labelEntries.forEach(({ key, label }) => {
    const [r, g, b] = resolveCategoryColor(
      key,
      label,
      layerStyle,
      [128, 128, 128, 1]
    )
    const color = rgbaComponentsToHex(r, g, b)
    const name = label
    const existingIndex = mergedClasses.findIndex(paintClass => paintClass.name === name)

    if (existingIndex >= 0) {
      mergedClasses[existingIndex] = { ...mergedClasses[existingIndex], color }
    } else {
      mergedClasses.push({
        id: Date.now() + newClassCounter,
        name,
        color,
        opacity: 1,
      })
      newClassCounter += 1
    }
  })

  return mergedClasses
}

/** Map raster values to existing paint classes by matching label names (for vector import). */
export function buildImportClassByValueFromGrid(
  grid: CategoricalTileGrid,
  datasetName: string,
  existingClasses: PaintClass[]
): Record<number, { classId: number; color: string }> {
  const classByValue: Record<number, { classId: number; color: string }> = {}
  const labelEntries = grid.labels ? Array.from(grid.labels.entries()) : []

  labelEntries
    .filter(([value]) => value !== 255)
    .forEach(([value, labelName]) => {
      const name = String(labelName ?? `${datasetName} ${value}`)
      const existing = existingClasses.find(paintClass => paintClass.name === name)
      if (!existing) return
      classByValue[value] = { classId: existing.id, color: existing.color }
    })

  return classByValue
}
